import './ShopifyConnections.scss';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdRefresh } from 'react-icons/md';
import { Badge } from '@Components/Badge';
import { Button } from '@Components/Button';
import { CopyButton } from '@Components/CopyButton';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Page } from '@Components/Page';
import { Skeleton, TableSkeleton } from '@Components/Skeleton/Skeleton';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { useSetError } from '@src/redux/modals';
import { formatLinkExpiry } from '@src/utils/link-expiry-utils';
import {
  EXISTING_CONNECTION_NOTES,
  INSTALL_LINK_VALID_DAYS,
  ShopifyAccountRow,
  ShopifyInstallLink
} from './shopify-connections-constants';
import {
  beginShopifyInstall,
  useAllShopifyAccounts,
  useCompanyIds
} from './use-shopify-connections';

/**
 * Internal tool for connecting a client's Shopify store, across all companies. An internal user
 * picks the company, generates a signed install link and sends it to the client, who authorizes in
 * their own Shopify admin. Shopify then redirects straight to the bpm-lambdas shopifyOauthCallback,
 * which stores the token -- so, unlike the Amazon flow, there is no frontend callback page and this
 * page never navigates to the link.
 */
export const ShopifyConnections = () => (
  <Page pageType='Shopify Connections' title='Shopify Connections' minHeight='600px'>
    <div className='shopify-connections-page'>
      <InstallLinkCard />
      <ConnectedStoresCard />
    </div>
  </Page>
);

const InstallLinkCard = () => {
  const setError = useSetError();
  const { data: companyIds = [], error: companyError } = useCompanyIds();
  const [company, setCompany] = useState('');
  const [store, setStore] = useState('');
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState<ShopifyInstallLink>();

  useEffect(() => {
    if (companyError) {
      setError({
        message: `Failed to fetch companies: ${companyError.message}`,
        reportError: companyError
      });
    }
  }, [companyError, setError]);

  const companyOptions = useMemo(
    () => companyIds.map(value => ({ value, label: value })),
    [companyIds]
  );

  const generateLink = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setLink(undefined);
      try {
        setLink(await beginShopifyInstall(company, store.trim()));
      } catch (err) {
        setError({
          message: `Failed to generate Shopify install link: ${err.message}`,
          reportError: err
        });
      } finally {
        setLoading(false);
      }
    },
    [company, store, setError]
  );

  return (
    <div className='shopify-card'>
      <div className='shopify-card-title'>Generate install link</div>
      <p className='shopify-card-help'>
        Select the company, then enter the company&apos;s store (e.g. <code>tinuiti</code> or{' '}
        <code>tinuiti.myshopify.com</code>) and send them the link. They must open it while logged
        into that store&apos;s Shopify admin. The link expires {INSTALL_LINK_VALID_DAYS} days after
        it is generated.
      </p>
      <Form className='install-link-form' onSubmit={generateLink}>
        <div className='install-link-company'>
          <Dropdown
            size={Size.LG}
            label='Company'
            placeholder='Select a company…'
            menuPortalTarget={document.body}
            searchable
            clearable
            value={company || null}
            options={companyOptions}
            onChange={value => setCompany(value ?? '')}
          />
        </div>
        <Form.Control
          aria-label='Shopify store'
          placeholder='Store name or *.myshopify.com domain'
          value={store}
          onChange={e => setStore(e.target.value)}
        />
        <Button size={Size.LG} type='submit' disabled={!company || !store.trim()} loading={loading}>
          Generate link
        </Button>
      </Form>
      {link && <InstallLinkResult link={link} company={company} />}
    </div>
  );
};

interface InstallLinkResultProps {
  link: ShopifyInstallLink;
  company: string;
}

const InstallLinkResult = ({ link, company }: InstallLinkResultProps) => {
  const note = EXISTING_CONNECTION_NOTES[link.existingConnection];

  return (
    <div className='install-link-result'>
      <div className='install-link-row'>
        <Form.Control aria-label='Install link' readOnly value={link.installUrl} />
        <CopyButton label='Copy' text={link.installUrl} />
      </div>
      <div className='install-link-meta'>
        <span>
          For <strong>{link.store}</strong> ({company}). {formatLinkExpiry(link.expiresAt)}.
        </span>
        {note && <Badge design={Intent.WARNING}>{note}</Badge>}
      </div>
    </div>
  );
};

const NoStoresRow = () => (
  <div className='no-shopify-stores'>No Shopify stores are connected yet.</div>
);

const ConnectedStoresCard = () => {
  const setError = useSetError();
  const { data: accounts, isLoading, isFetching, error, refetch } = useAllShopifyAccounts();

  useEffect(() => {
    if (error) {
      setError({ message: `Failed to fetch Shopify stores: ${error.message}`, reportError: error });
    }
  }, [error, setError]);

  const headers = useMemo(
    () => [
      { name: 'company', label: 'Company', flex: 1 },
      { name: 'account_id', label: 'Store', flex: 2 },
      {
        name: 'enabled',
        label: 'Status',
        flex: 1,
        renderer: (row: ShopifyAccountRow) => (
          <Badge design={row.enabled ? Intent.SUCCESS : Intent.ERROR}>
            {row.enabled ? 'Connected' : 'Disabled'}
          </Badge>
        )
      }
    ],
    []
  );

  return (
    <div className='shopify-card'>
      <div className='shopify-card-header'>
        <div className='shopify-card-title'>Connected stores</div>
        {/* Nothing tells this page when a client finishes installing, so refreshing is manual. */}
        <Button
          appearance={Appearance.OUTLINED}
          leadingElement={<MdRefresh />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          Refresh
        </Button>
      </div>
      <div className='shopify-stores-table'>
        {isLoading ? (
          <Skeleton>
            <TableSkeleton />
          </Skeleton>
        ) : (
          <BPMTable headers={headers} data={accounts ?? []} noRowsRenderer={NoStoresRow} />
        )}
      </div>
    </div>
  );
};
